import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Api, createInvitation, getSentInvitations, InvitationsListResponseDto } from '@taxora-api';
import { CreateInvitation, LoadActivity, ResetActivity } from './activity.actions';

interface ActivityStateModel {
  result: InvitationsListResponseDto | null;
  isLoading: boolean;
  hasError: boolean;
  isSending: boolean;
}

@State<ActivityStateModel>({
  name: 'activityState',
  defaults: { result: null, isLoading: false, hasError: false, isSending: false },
})
@Injectable()
export class ActivityState {
  private readonly api = inject(Api);

  @Selector()
  static result(state: ActivityStateModel): InvitationsListResponseDto | null {
    return state.result;
  }

  @Selector()
  static isLoading(state: ActivityStateModel): boolean {
    return state.isLoading;
  }

  @Selector()
  static hasError(state: ActivityStateModel): boolean {
    return state.hasError;
  }

  @Selector()
  static isSending(state: ActivityStateModel): boolean {
    return state.isSending;
  }

  @Action(LoadActivity)
  async load(
    { patchState }: StateContext<ActivityStateModel>,
    { params }: LoadActivity,
  ): Promise<void> {
    patchState({ isLoading: true, hasError: false });
    try {
      const result = await this.api.invoke(getSentInvitations, params);
      patchState({ result });
    } catch (error) {
      console.error('Failed to load activity', error);
      patchState({ hasError: true });
    } finally {
      patchState({ isLoading: false });
    }
  }

  @Action(ResetActivity)
  reset({ patchState }: StateContext<ActivityStateModel>): void {
    patchState({ result: null, isLoading: false, hasError: false, isSending: false });
  }

  @Action(CreateInvitation)
  async send(
    { patchState, dispatch }: StateContext<ActivityStateModel>,
    { email, senderOrganizationId }: CreateInvitation,
  ): Promise<void> {
    patchState({ isSending: true });
    try {
      await this.api.invoke(createInvitation, { body: { email, senderOrganizationId } });
      // Fire-and-forget: list refresh must not block the dialog or propagate errors
      dispatch(new LoadActivity({ organizationId: senderOrganizationId }));
    } catch (error) {
      console.error('Failed to send invitation', error);
      throw error;
    } finally {
      patchState({ isSending: false });
    }
  }
}
